import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, Text } from '@modusign/design-system';
import { ReactNode, useState } from 'react';

import { ResourceModalBody, ResourceModalFooter } from '@app/components/Modal/ResourceModal';
import { ResourceRoleSubject } from '@app/features/authorization';
import AddAssigneeForm from '@app/features/authorization/components/PermissionAssigneeListModal/AddAssigneeForm';
import ResourceRoleTypeSelect from '@app/features/authorization/components/ResourceRoleTypeSelect';
import { ResourceRole } from '@app/features/authorization/types';

interface RoleOptions<T> {
  roleName: T;
  name: string;
  content: string;
  icon: ReactNode;
}

/**
 * preGrantedResourceRoles는 이미 공유 중인 대상 (선택 해제 불가)
 * initialResourceRoles는 모달에서 미리 체크될 대상 (선택 해제 가능)
 */
interface AddResourceRoleModalContentProps<T> {
  roleOptions?: RoleOptions<T>[];
  preGrantedResourceRoles?: ResourceRoleSubject[];
  initialResourceRoles?: ResourceRoleSubject[];
  onSubmit: (resourceRoles: ResourceRole<T>[]) => void;
  onCancel: () => void;
}

export default function AddResourceRoleModalContent<T extends string>({
  roleOptions,
  preGrantedResourceRoles,
  initialResourceRoles,
  onSubmit,
  onCancel,
}: AddResourceRoleModalContentProps<T>) {
  const [resourceRoleSubjects, setResourceRoleSubjects] = useState<ResourceRoleSubject[]>(
    initialResourceRoles ?? []
  );
  const [roleName, setRoleName] = useState<T>('VIEWER' as T);

  return (
    <>
      <ResourceModalBody>
        <AddAssigneeForm
          preGrantedResourceRoles={preGrantedResourceRoles}
          subjects={resourceRoleSubjects}
          onChange={setResourceRoleSubjects}
        >
          {roleOptions && (
            <ResourceRoleTypeSelect
              roleOptions={roleOptions}
              selectedRoleName={roleName}
              setSelectedRoleName={setRoleName}
            />
          )}
        </AddAssigneeForm>
      </ResourceModalBody>
      <ResourceModalFooter alignItems="center" justifyContent="flex-end">
        {resourceRoleSubjects.some(
          (rr) => rr.subjectType === 'GROUP' && rr.groupType === 'ALL_MEMBERS'
        ) && (
          <Flex flex="auto" sx={{ gap: 1 }}>
            <Text color="textPrimaryDark">
              <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 3 }} />
              <Text fontSize="14px" fontWeight="medium">
                주의
              </Text>
            </Text>
            <Text>모든 멤버에게 공유됩니다.</Text>
          </Flex>
        )}
        <Button weight="light" onClick={onCancel}>
          취소
        </Button>
        <Button
          disabled={resourceRoleSubjects.length === 0}
          ml={3}
          variant="primary"
          weight="light"
          onClick={() =>
            onSubmit(
              resourceRoleSubjects.map((subject) => ({
                subject,
                name: roleName,
              }))
            )
          }
        >
          공유
        </Button>
      </ResourceModalFooter>
    </>
  );
}
